import React, { useState, useEffect, Fragment, useContext } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { Box, xcss } from '@atlaskit/primitives';

import { getBlankPageQuickActionsExperimentEnabled } from '@confluence/blank-page-quick-actions';
import { EditorTitle } from '@confluence/editor-title';
import { FlagsStateContainer } from '@confluence/flags';
import { useSpaceId } from '@confluence/space-utils';
import { useBooleanFeatureFlag } from '@confluence/session-data';
import { RoutesContext } from '@confluence/route-manager';
import { getCreatedWithTemplateQueryParamValue } from '@confluence/template-utils';
import {
	TitleAlignmentType,
	useTitleContentPropertiesForEditor,
} from '@confluence/custom-sites-extensions';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture';
import { LivePagesTitle } from '@confluence/editor-title/entry-points/LivePagesTitle';
import { useIsLivePage } from '@confluence/live-pages-utils/entry-points/useIsLivePage';
import { CUSTOM_SITES_PAGE_TITLE_FF } from '@confluence/emoji-title/entry-points/constants';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';
import { usePageSpaceKey } from '@confluence/page-context';

import { EditorTitleWithTitleAreaComponents } from './EditorTitleWithTitleAreaComponents';
import type { TitleAreaContentComponentsProps } from './titleareaContentComponentsTypes';
import { AlignmentButtonContainer } from './AlignmentButtonContainer';

const topLineBoxStylesMarginTopSpaceNegative300 = xcss({
	marginTop: 'space.negative.300',
});

const templateSuggestionsBoxStylesBase = xcss({
	minHeight: '32px',
	display: 'flex',
});

const templateSuggestionsBoxMarginsWithCustomSitesPageTitleFF = xcss({
	marginTop: 'space.negative.075',
	marginBottom: 'space.negative.075',
});

const templateSuggestionsBoxMarginsWithoutCustomSitesPageTitleFF = xcss({
	marginTop: 'space.negative.400',
});

const templateSuggestsBoxStylesJustifyContentCenter = xcss({
	justifyContent: 'center',
});

type TitleAreaComponentWithState = TitleAreaContentComponentsProps & {
	alignmentButtonContainer: AlignmentButtonContainer;
	coverPictureStateContainer: CoverPictureStateContainer;
	flags: FlagsStateContainer;
	contentComponentsDisabled?: boolean;
	isStatusDisconnected?: boolean;
};

const TitleAreaComponentWithState = ({
	title,
	breadcrumbsComponent,
	templateSuggestionsComponent,
	contentId,
	isDisabled,
	linkCreation,
	onChange,
	focusEditor,
	selectDocStart,
	insertNewLineAtDocStart,
	onFocusChanged,
	debounceOnChange,
	contentType,
	userOffline,
	isInPreview,
	isUnpublishedDraft,
	isPublishingContent,
	templateSuggestionsRef,
	templateButtonRef,
	isTemplateEditor = false,
	contentTopperComponent: headerActionsComponent,
	viewMode,
	contentClassificationMetadata,
	version,
	isEditorEmpty,
	alignmentButtonContainer,
	coverPictureStateContainer,
	flags,
	contentComponentsDisabled,
	isStatusDisconnected,
}: TitleAreaComponentWithState) => {
	const isCustomSitesPageTitleFFOn = useBooleanFeatureFlag(CUSTOM_SITES_PAGE_TITLE_FF);
	const spaceId = useSpaceId();
	const updateAlignmentState = alignmentButtonContainer?.toggleAlignmentButton;
	const updateCoverPictureWidthState = coverPictureStateContainer?.toggleCoverPictureWidthButton;

	const hideWhileLoading = !(window.__SSR_RENDERED__ || process.env.REACT_SSR);
	const { isTitleAlignmentLoading } = useTitleContentPropertiesForEditor({
		contentId,
		updateAlignmentState,
		updateCoverPictureWidthState,
		loadTitleContentProperties: isCustomSitesPageTitleFFOn,
		contentType,
		flags,
	});

	const [localUserOffline, setLocalUserOffline] = useState<boolean>(false);
	const { getQueryParams } = useContext(RoutesContext);
	const isLivePage = useIsLivePage();
	const [spaceKey = ''] = usePageSpaceKey();

	const checkBPQAEligibility = () => {
		// Get the blank page quick actions tenant and user level experiment cohorts, only fire exposure event
		const { cohort } = getBlankPageQuickActionsExperimentEnabled({
			shouldFireExperimentExposure: true,
		});
		// hide popular templates if user is in either variant cohort
		return cohort != 'variant1' && cohort != 'variant2';
	};

	// Should not show template suggestions for variant 2 of blank page quick actions experiment
	const isTemplatesSuggestionsEnabled =
		templateSuggestionsComponent &&
		isUnpublishedDraft &&
		!getCreatedWithTemplateQueryParamValue(getQueryParams) &&
		contentType === 'page' &&
		checkBPQAEligibility();

	const contentClassificationFromHook = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
	});

	const contentClassification = !contentClassificationMetadata
		? contentClassificationFromHook
		: contentClassificationMetadata;

	const { shouldDisplayClassification } = contentClassification;

	// Connection banner height has a delay to appear, adjust the draft banner height to reflect this delay
	useEffect(() => {
		const DELAY_TIMEOUT = 3000;
		const delayTimeout: ReturnType<typeof setTimeout> = setTimeout(
			() => setLocalUserOffline(userOffline),
			DELAY_TIMEOUT,
		);

		return () => {
			clearTimeout(delayTimeout);
		};
	}, [userOffline]);

	const renderTitleComponent = () => {
		if (isCustomSitesPageTitleFFOn && isTitleAlignmentLoading && hideWhileLoading) {
			return null;
		}
		return (
			<Subscribe to={[FlagsStateContainer, AlignmentButtonContainer]}>
				{(flags: FlagsStateContainer, alignmentButtonContainer: AlignmentButtonContainer) => {
					const isTitleCenterAligned =
						isCustomSitesPageTitleFFOn &&
						alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER;

					if (isLivePage) {
						return (
							<LivePagesTitle
								title={title ?? ''}
								contentId={contentId}
								isDisabled={isDisabled}
								focusEditor={focusEditor}
								selectDocStart={selectDocStart}
								insertNewLineAtDocStart={insertNewLineAtDocStart}
								onFocusChanged={onFocusChanged}
								viewMode={viewMode}
								isTitleCenterAligned={isTitleCenterAligned}
								version={version}
							/>
						);
					} else {
						return (
							<EditorTitle
								title={title}
								contentId={contentId}
								isDisabled={isDisabled}
								onChange={onChange}
								focusEditor={focusEditor}
								selectDocStart={selectDocStart}
								insertNewLineAtDocStart={insertNewLineAtDocStart}
								onFocusChanged={onFocusChanged}
								debounceOnChange={debounceOnChange}
								contentType={contentType}
								userOffline={userOffline}
								isInPreview={isInPreview}
								isTemplateEditor={isTemplateEditor}
								templateSuggestionsRef={templateSuggestionsRef}
								templateButtonRef={templateButtonRef}
								isTemplatesSuggestionsEnabled={isTemplatesSuggestionsEnabled}
								flags={flags?.state?.flags ?? []}
								isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
								isTitleCenterAligned={isTitleCenterAligned}
								isUnpublishedDraft={isUnpublishedDraft}
								contentClassificationMetadata={contentClassification}
								hasClassification={shouldDisplayClassification}
								isEditMode
								spaceKey={spaceKey}
								spaceId={spaceId}
							/>
						);
					}
				}}
			</Subscribe>
		);
	};

	return (
		<Fragment>
			{isTemplateEditor && (
				<Box
					xcss={[localUserOffline && topLineBoxStylesMarginTopSpaceNegative300]}
					testId="breadcrumbs-wrapper"
				>
					{breadcrumbsComponent}
				</Box>
			)}
			<EditorTitleWithTitleAreaComponents
				contentId={contentId}
				isPublishingContent={isPublishingContent}
				contentType={contentType}
				userOffline={userOffline}
				isTemplateEditor={isTemplateEditor}
				isTitleDisabled={isDisabled}
				isUnpublishedDraft={isUnpublishedDraft}
				isTemplateSuggestionsEnabled={isTemplatesSuggestionsEnabled}
				isTitleCenterAligned={
					isCustomSitesPageTitleFFOn &&
					alignmentButtonContainer.state.alignmentButtonState === TitleAlignmentType.CENTER
				}
				linkCreation={linkCreation}
				viewMode={viewMode}
				contentTopperComponent={!isTemplateEditor && headerActionsComponent}
				contentClassificationMetadata={contentClassification}
				hasClassification={shouldDisplayClassification}
				spaceKey={spaceKey}
				isEditorEmpty={isEditorEmpty}
				isTitleAlignmentLoading={isTitleAlignmentLoading && hideWhileLoading}
				contentComponentsDisabled={contentComponentsDisabled}
				isStatusDisconnected={isStatusDisconnected}
				coverPictureState={coverPictureStateContainer}
			>
				{renderTitleComponent()}
			</EditorTitleWithTitleAreaComponents>
			{isTemplatesSuggestionsEnabled ? (
				<Box
					xcss={[
						templateSuggestionsBoxStylesBase,
						isCustomSitesPageTitleFFOn
							? templateSuggestionsBoxMarginsWithCustomSitesPageTitleFF
							: templateSuggestionsBoxMarginsWithoutCustomSitesPageTitleFF,
						isCustomSitesPageTitleFFOn &&
							alignmentButtonContainer.state.alignmentButtonState ===
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								TitleAlignmentType.CENTER &&
							templateSuggestsBoxStylesJustifyContentCenter,
					]}
				>
					{templateSuggestionsComponent}
				</Box>
			) : null}
		</Fragment>
	);
};

export const TitleAreaContentComponents = (props: TitleAreaContentComponentsProps) => {
	return (
		<Subscribe to={[AlignmentButtonContainer, CoverPictureStateContainer, FlagsStateContainer]}>
			{(
				alignmentButtonContainer: AlignmentButtonContainer,
				coverPictureStateContainer: CoverPictureStateContainer,
				flags: FlagsStateContainer,
			) => (
				<TitleAreaComponentWithState
					{...props}
					alignmentButtonContainer={alignmentButtonContainer}
					coverPictureStateContainer={coverPictureStateContainer}
					flags={flags}
				/>
			)}
		</Subscribe>
	);
};
